import React, { useCallback, useEffect, useState, memo } from 'react';
import { fetchSettings, updateSettings } from '../../api/settings';
import { BetaBadge } from '../common/Badges';
import { FormGroup, FormToggle, WarningBox } from '../common/form';
import { motion } from 'framer-motion';
import PillButton from '../common/button/PillButton';
import PersonalMemoriesModal from './PersonalMemoriesModal';

import styles from '../agents/styles/AgentSettings.module.css';

const ProfileSettings = () => {
    const [profile, setProfile] = useState({ memoryInChats: true, memoryInAgents: true });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);

    const loadSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await fetchSettings('profile');
            const p = data?.profile || { memoryInChats: true, memoryInAgents: true };
            setProfile({
                memoryInChats: p?.memoryInChats ?? true,
                memoryInAgents: p?.memoryInAgents ?? true,
            });
        } catch (e) {
            setError('Failed to load profile settings');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleToggle = useCallback(async key => {
        try {
            setIsSaving(true);
            const next = { ...profile, [key]: !profile[key] };
            await updateSettings({ profile: next });
            setProfile(next);
        } catch (e) {
            setError('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    }, [profile]);

    if (isLoading) {
        return <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <p>Loading settings...</p>
        </motion.div>
    }

    return (
        <div className={styles.tabContent} style={{ padding: '0 2rem' }}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                    Memory Settings <BetaBadge />
                </h3>
                <p className={styles.sectionDescription}>
                    Control how your personal memory is used in chats and with agents. We always store memory, but when disabled it won't be used in conversations.
                </p>

                <PillButton
                    size="small"
                    variant="secondary"
                    onClick={() => setIsMemoriesOpen(true)}
                    style={{ marginBottom: '1.5rem' }}
                >
                    View Personal Memories
                </PillButton>

                <FormGroup>
                    <FormToggle
                        checked={!!profile.memoryInChats}
                        onChange={() => handleToggle('memoryInChats')}
                        disabled={isSaving}
                        title="Memory in Chats"
                        description="Use your personal memory during standard chats."
                    />
                </FormGroup>

                <FormGroup>
                    <FormToggle
                        checked={!!profile.memoryInAgents}
                        onChange={() => handleToggle('memoryInAgents')}
                        disabled={isSaving}
                        title="Memory in Agents"
                        description="Use your personal memory when talking to agents (only if the agent has memory enabled)."
                    />
                </FormGroup>

                {error && (
                    <WarningBox>{error}</WarningBox>
                )}
            </div>

            <PersonalMemoriesModal isOpen={isMemoriesOpen} onClose={() => setIsMemoriesOpen(false)} />
        </div>
    );
};

export default memo(ProfileSettings);
